import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Member, deleteMembers, loadAudience } from "../audience-api";
import { useConfirm } from "../components/Dialogs";
import { useAddToast } from "../components/Toasts";
import { ClickShield, Column, Table } from "../table/Table";
import { AudienceContext, useAudience } from "./AudienceStore";
import { MemberPageContext, MemberPageStore, getRange, useIsSelected, useToggle, useTotal } from "./MemberPageStore";
import { ValidateContactButton } from "./contact-validation";
import { MemberNavigation, MessagePreview, createMemberNavigation } from "./message-preview/MessagePreview";
import { MessageBox } from "../comps/MessageBox";
import { ReactTooltip } from "tooltip";
import { BackButtonBar } from "../comps/BackButton";


export function useHeaders(rows:Member[]):string[]{
    return useMemo(() =>{
        if(rows == null || rows.length == 0) return null;
        let headers = new Set(rows.map(r => Object.keys(r.sourceMap)).flat());
        return Array.from(headers.values());
    }, [rows])
}

function WAStatus({member}:{member:Member}){
    if(member.isWAPhone === null) return <i className="far fa-square text-muted"></i>
    if(member.isWAPhone) return <ClickShield><ReactTooltip tooltip="WhatsApp Verificado" delay={300} placement="top-start"><i className="far fa-square-check"></i></ReactTooltip></ClickShield>
    return <i className="far fa-square"></i>
}

function Checker({index}:{index:number}){
    const isSelected = useIsSelected()
    const toggle = useToggle();
    return <ClickShield>
        <input className="form-check-input" type="checkbox" checked={isSelected(index)} onChange={() => toggle(index)}  />
    </ClickShield>
}

function MainChecker(){
    const [total, selected] = useTotal();
    const {dispatch} = useContext(MemberPageContext);
    const ok = selected == total

    function onChange(){
        if(ok) 
            dispatch({type:"setSelected", payload:[]})
        else
            dispatch({type:"setSelected", payload:getRange(0, total - 1)})
    }

    return <input className="form-check-input" type="checkbox" checked={ok} onChange={onChange}  />
}

export function useColumns(rows:Member[]):Column<Member>[] {
    const headers = useHeaders(rows);
    return useMemo(() => {
        if(headers == null) return null;
        const columns:Column<Member>[] = [
                {id:"check", thGroup:"th-check", label: <MainChecker />, render: (r, i) => <Checker index={i} /> },
                {id:"isWA", colGroup:"align-middle", label:"WA", render: (r) => <WAStatus member={r} /> }
        ]
        return [...columns, ... (headers.map(h => ({id:h, label:h, render: (r) => <>{r.sourceMap[h]}</>}) as Column<Member>)) ]

    }, [headers]);
}

function AudienceMemberRender(){
    const audience = useAudience();
    const variables = useHeaders(audience.members);
    const columns = useColumns(audience.members)
    const [nav, setNav] = useState<MemberNavigation>(null);

    const [, , setTotal] = useTotal();
    useEffect(() => { 
        setTotal(audience.members?.length || 0);
    }, [audience.members])

    if(columns == null) return <MessageBox className="my-4">
                <div className="mb-2">Não há contatos importados ainda.</div>
                Clique no botão abaixo para fazer sua primeira importação.
        </MessageBox>

    function showMessagePreview(m:Member){
        const index = audience.members.indexOf(m)
        setNav(createMemberNavigation(audience.members, index));
    }

    const count = audience.members.reduce((c, m) => c + (m.isWAPhone ? 1 : 0), 0);

    return <>
        <div className="mb-2">Number of members <b>{audience.members.length}</b> | WA active: <b>{count}</b></div>
        <div className="table-view" style={{maxHeight: "calc(100vh - 312px)", position: "relative", overflow:"auto"}}>
            <Table columns={columns} data={audience.members} onClick={showMessagePreview} />
        </div>
        {nav && <MessagePreview variables={variables} onHide={() => setNav(null)} navigation={nav} />}
    </>
}

export function AudiencePage(){
    const {id} = useParams();
    const {dispatch, state} = useContext(AudienceContext);
    const {audience} = state;
    const [version, setVersion] = useState(0);
    const navigate = useNavigate()
    
    useEffect(function(){
        (async () => {
            const audience = await loadAudience(parseInt(id));
            dispatch({type: "setAudience", payload: audience})

        })();
    }, [version]);

    if(!audience) 
        return <div className="text-muted">Loading...</div>

    return <MemberPageStore>
        <BackButtonBar><h3>Público <small><i className="fa fa-chevron-right crumb-arrow"></i> {audience.name}</small></h3></BackButtonBar>
        {audience.members && <AudienceMemberRender />}
        <div className="actions">
            <button className="btn btn-primary" onClick={() => navigate("./importar")}><i className="fa fa-cloud-arrow-up me-2"></i>  Importar Contatos</button>
            {false && <ValidateContactButton audience={audience} shouldUpdate={() => setVersion(version + 1)} />}
            {audience.members?.length > 0 && <DeleteMembersButton />}
        </div>
    </MemberPageStore>
}

function DeleteMembersButton(){
    const audience = useAudience();
    const [total] = useTotal();
    const {state: {selected}, dispatch} = useContext(MemberPageContext);
    const confirm = useConfirm( selected.length < total ? <>Deseja realmente remover <b>{selected.length}</b> de <b>{total}</b> contatos?</> : "Deseja realmente remover todos os contatos?", {title:audience.name, buttonClass:"btn-danger", ok:"Remover Contatos"});
    const ac  = useContext(AudienceContext);
    const addToast = useAddToast();

    if(selected.length == 0) return null;

    async function doDelete(){
        const ok = await confirm();
        if(!ok) return;
        
        if(selected.length < total){
            let toDelete = audience.members.filter((m, i) => selected.includes(i));
            let newMembers = audience.members.filter((m, i) => !selected.includes(i))
            await deleteMembers(audience.id, {ids: toDelete.map(m => m.id)});
            ac.dispatch({type: "setAudience", payload: {...audience, members: newMembers}})
            dispatch({type: "setSelected", payload: []});
            return;
        }

        await deleteMembers(audience.id);
        addToast(`Contatos removidos!`, 'bg-success');
        ac.dispatch({type: "setAudience", payload: {...audience, members: []}})
    }

    return <button className="btn btn-danger" onClick={doDelete}><i className="fa fa-trash me-2"></i> Deletar Contatos</button>
}